// Skeleton (CONTRACT "States": '.skel: matches the call row height, three rows
// fading, "Loading calls" with elapsed after 2 s'). It is the Loading shape for
// a surface that has nothing on it yet.
//
// Why it exists as its own primitive: fourteen destinations rendered
// HonestState variant="Loading", which draws the same dashed frame as Provider
// unavailable and Permission denied. A first load therefore read as a failure
// when nothing had failed. The skeleton instead reserves the space the loaded
// content will take, at the row height of the surface it stands in, so the
// rows land where the shimmer was.
//
// `shape` names that surface. It is not decoration: each shape reads a
// different row token, so a table skeleton reserves 32 px rows in Default and
// 28 in Compact while a call skeleton reserves 36 and 28, and the loaded
// surface lands without a jump.
//
// The elapsed line comes from states/useElapsed, the same clock the auth
// screens and the honest-state blocks use, so a slow request prints the same
// sentence after the same 2 s wherever the reader is.
import { ELAPSED_AFTER_MS, useElapsedLine } from '../states/useElapsed';
import { cx } from './cx';
import './Skeleton.css';

export const SKELETON_ELAPSED_AFTER_MS = ELAPSED_AFTER_MS;

export const SKELETON_SHAPES = ['call', 'list', 'table', 'panel', 'graph'] as const;
export type SkeletonShape = (typeof SKELETON_SHAPES)[number];

export interface SkeletonProps {
  // Names the request in flight, for example "Loading calls" or the state
  // reason with its full stop trimmed. The elapsed line appends to it.
  readonly label?: string;
  // The surface the skeleton stands in. Drives the row height it reserves.
  readonly shape?: SkeletonShape;
  // Rows to draw. Ignored by the graph shape, which is one canvas.
  readonly rows?: number;
  readonly className?: string;
}

// How many rows each surface shows before the reader has to scroll for more.
// A table shows a header plus rows, so it opens with one more than a list.
const DEFAULT_ROWS: Readonly<Record<SkeletonShape, number>> = {
  call: 3,
  list: 3,
  table: 4,
  panel: 3,
  graph: 1,
};

// A caller usually has a state reason to hand ("Waiting for the credential
// list."), which is a whole sentence. The accessible name keeps it as written;
// the visible line drops the full stop so the elapsed clause reads as part of
// the same sentence rather than starting a new one.
function joinable(label: string): string {
  return label.replace(/[.\s]+$/u, '');
}

export function Skeleton({ label = 'Loading calls', shape = 'call', rows, className }: SkeletonProps) {
  // Always on: the component only mounts while its surface is loading, and it
  // unmounts the moment the answer arrives, which drops the counter with it.
  const elapsed = useElapsedLine(true);
  const count = shape === 'graph' ? 1 : Math.max(1, rows ?? DEFAULT_ROWS[shape]);

  return (
    <div role="status" aria-label={label} data-shape={shape} className={cx('skel', className)}>
      {Array.from({ length: count }, (_, index) => (
        <div key={index} className="skel-row" aria-hidden="true" />
      ))}
      {elapsed ? (
        <p className="skel-lbl">
          {joinable(label)}, {elapsed}
        </p>
      ) : null}
    </div>
  );
}
